import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Spinner } from 'react-bootstrap';
import { EChart as ReactECharts } from '../charts/EChart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRocket,
  faTrashCan,
  faFlask,
  faPenToSquare
} from '@fortawesome/free-solid-svg-icons';
import ProductColorDot from '../common/ProductColorDot';
import { useRemainingMaterialSummary } from '../../features/jobs/hooks/useProductionDashboardStats';
import {
  useFutureProducts,
  useFutureDashboard,
  useFutureMutations
} from '../../features/future-products/hooks';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider, {
  useAdvanceTableContext
} from '../../providers/AdvanceTableProvider';
import AdvanceTable from '../base/AdvanceTable';
import AdvanceTableFooter from '../base/AdvanceTableFooter';
import FilterTab from '../common/FilterTab';
import SearchBox from '../common/SearchBox';
import { useAppContext } from '../../providers/AppProvider';
import { formatProductLabel, formatCoreSpec } from '../../helpers/productLabel';

// ── shared little helpers (exported so the Future-Products page reuses them) ──
export const fmt = n =>
  Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
export const num0 = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
export const r2 = n => Number((Number(n) || 0).toFixed(2));

const DEST_BG = { ORDER: 'primary', STOCK: 'secondary', MIXED: 'warning' };

const PHASE_SHORT = {
  pre_stranding: 'Pre-str',
  conductor_isolation: 'Isol',
  rope: 'Rope',
  interior_sheat: 'Interior',
  armouring: 'Armour',
  exterior_sheat: 'Exterior',
  coiling: 'Coil',
  rewinding: 'Rewind'
};
const phaseShort = n => PHASE_SHORT[n] || String(n).replace(/_/g, ' ');

const phaseCoveragePct = (phase, jobLen) => {
  if (!jobLen) return 0;
  if (Array.isArray(phase.conductors) && phase.conductors.length) {
    const perCond = phase.conductors.map(
      c =>
        (c.execution || []).reduce((s, e) => s + num0(e.plannedLength), 0) /
        jobLen
    );
    return Math.max(0, Math.min(100, Math.round(Math.min(...perCond) * 100)));
  }
  const sum = (phase.execution || []).reduce(
    (s, e) => s + num0(e.plannedLength),
    0
  );
  return Math.max(0, Math.min(100, Math.round((sum / jobLen) * 100)));
};

const PlannedPhases = ({ product }) => {
  const jobLen = num0(product.plannedLength);
  const phases = product.phasePlan || [];
  if (!phases.length) return <span className="text-500">—</span>;
  return (
    <div className="d-flex flex-wrap align-items-center gap-1">
      {phases.map((p, i) => {
        const name = p.name || p.phase;
        const pct = phaseCoveragePct(p, jobLen);
        const full = pct >= 100;
        const cls = full
          ? 'bg-success-subtle text-success border border-success'
          : pct > 0
            ? 'bg-info-subtle text-info border border-info'
            : 'bg-body-secondary text-muted border';
        return (
          <span key={name + i} className="d-inline-flex align-items-center gap-1">
            {i > 0 && <span className="text-300">›</span>}
            <span
              className={`px-2 py-1 rounded ${cls}`}
              style={{ fontSize: 10 }}
              title={`${name}: ${pct}% programmed`}
            >
              {phaseShort(name)}
              {full ? ' ✓' : pct > 0 ? ` ${pct}%` : ''}
            </span>
          </span>
        );
      })}
    </div>
  );
};

const ConsumablesCell = ({ copper, pvc, steel }) => {
  const items = [
    { label: 'Cu', kg: Number(copper) || 0, color: '#b5651d' },
    { label: 'PVC', kg: Number(pvc) || 0, color: '#2f9e44' },
    { label: 'BLR', kg: Number(steel) || 0, color: '#868e96' }
  ].filter(x => x.kg > 0);
  if (!items.length) return <span className="text-500">—</span>;
  const max = Math.max(...items.map(x => x.kg));
  return (
    <div className="d-flex flex-column gap-1" style={{ minWidth: 170 }}>
      {items.map(x => (
        <div key={x.label} className="d-flex align-items-center gap-2">
          <span className="fs-10 text-700" style={{ width: 34 }}>
            {x.label}
          </span>
          <div
            className="flex-grow-1 rounded-pill"
            style={{ height: 6, background: '#e9ecef' }}
          >
            <div
              style={{
                width: `${(x.kg / max) * 100}%`,
                height: '100%',
                background: x.color,
                borderRadius: 999
              }}
            />
          </div>
          <span
            className="fs-10 fw-semibold"
            style={{ width: 62, textAlign: 'right' }}
          >
            {fmt(x.kg)} kg
          </span>
        </div>
      ))}
    </div>
  );
};

const buildFutureColumns = ({ perProduct, launch, remove, navigate }) => [
  {
    id: 'product',
    header: 'Product',
    accessorFn: p =>
      `${formatProductLabel(p)} ${(p.clients || []).join(' ')}`,
    cell: ({ row: { original: p } }) => (
      <div className="d-flex flex-column">
        <span className="fw-bold fs-8 d-inline-flex align-items-center gap-2">
          <ProductColorDot row={p} />
          {p.productLabel || p.name}
        </span>
        <span className="text-700 fs-10">
          {formatCoreSpec(p)} mm²
        </span>
      </div>
    ),
    meta: {
      cellProps: { className: 'py-3 ps-3' },
      headerProps: { style: { minWidth: 170 }, className: 'ps-3' }
    }
  },
  {
    id: 'destination',
    header: 'Destination',
    accessorFn: p => (p.clients?.length ? p.clients.join(', ') : 'STOCK'),
    cell: ({ row: { original: p } }) => {
      const breakdown = p.destinationBreakdown || [];
      const stock = Number(p.stockMeters) || 0;
      if (!breakdown.length && stock <= 0) {
        return (
          <Badge variant="phoenix" bg="secondary">
            STOCK
          </Badge>
        );
      }
      return (
        <div className="d-flex flex-column align-items-start gap-1">
          {breakdown.map((b, i) => (
            <Badge
              key={i}
              variant="phoenix"
              bg="primary"
              className="fs-10"
              title={`${b.client}: ${fmt(b.meters)} m allocated for this client`}
            >
              {String(b.client).toUpperCase()} ·{' '}
              <span className="fw-bold">{fmt(b.meters)} m</span>
            </Badge>
          ))}
          {stock > 0 && (
            <Badge
              variant="phoenix"
              bg="success"
              className="fs-10"
              title={`${fmt(stock)} m for stock`}
            >
              STOCK · <span className="fw-bold">{fmt(stock)} m</span>
            </Badge>
          )}
        </div>
      );
    },
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { minWidth: 160 } }
    }
  },
  {
    accessorKey: 'plannedLength',
    header: 'Length (m)',
    cell: ({ row: { original: p } }) => (
      <span className="fw-semibold">{fmt(p.plannedLength)}</span>
    ),
    meta: {
      cellProps: { className: 'text-end py-3 pe-3' },
      headerProps: { style: { width: '10%' }, className: 'text-end pe-3' }
    }
  },
  {
    id: 'phases',
    header: 'Planned phases',
    enableSorting: false,
    cell: ({ row: { original: p } }) => <PlannedPhases product={p} />,
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { minWidth: 240 } }
    }
  },
  {
    id: 'consumables',
    header: 'Consumables',
    enableSorting: false,
    cell: ({ row: { original: p } }) => {
      const m = perProduct.get(p.futureId) || {};
      return (
        <ConsumablesCell
          copper={m.copper_kg}
          pvc={m.pvc_kg}
          steel={m.steel_kg}
        />
      );
    },
    meta: {
      cellProps: { className: 'py-3' },
      headerProps: { style: { minWidth: 190 } }
    }
  },
  {
    id: 'action',
    header: '',
    enableSorting: false,
    cell: ({ row: { original: p } }) => {
      const launching =
        launch.isPending && launch.variables?.futureId === p.futureId;
      const removing = remove.isPending && remove.variables === p.futureId;
      return (
        <div className="text-end text-nowrap pe-3">
          <Button
            size="sm"
            variant="outline-primary"
            className="me-2"
            disabled={!p._productSpec}
            onClick={() =>
              navigate('/apps/project-management/create-launch', {
                state: {
                  future: true,
                  futureEditId: p.futureId,
                  prefillStandardData: [p._productSpec]
                }
              })
            }
            title="Open the wizard to view / edit each execution, then re-save"
          >
            <FontAwesomeIcon icon={faPenToSquare} />
            <span className="ms-2">Preview / edit</span>
          </Button>
          <Button
            size="sm"
            variant="success"
            className="me-2"
            disabled={launching}
            onClick={() => launch.mutate(p)}
            title="Launch as a real job (allocates to orders + enqueues at machines, like Project Simulation) + remove from sandbox"
          >
            {launching ? (
              <Spinner size="sm" />
            ) : (
              <FontAwesomeIcon icon={faRocket} />
            )}
            <span className="ms-2">Quick launch</span>
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            disabled={removing}
            onClick={() => remove.mutate(p.futureId)}
            title="Discard this future product"
          >
            <FontAwesomeIcon icon={faTrashCan} />
          </Button>
        </div>
      );
    },
    meta: {
      cellProps: { className: 'text-end py-3' },
      headerProps: { style: { width: '16%' }, className: 'text-end pe-3' }
    }
  }
];

// merge REMAINING (open orders) + PREPARED (future sandbox) material by diameter/grade
export const mergeMaterial = (remainingArr, preparedArr, key) => {
  const labels = [];
  const rem = new Map();
  const prep = new Map();
  const seen = new Set();
  const add = (arr, map, kgKey) => {
    for (const x of arr || []) {
      const k = String(x[key] ?? '—');
      if (!seen.has(k)) {
        seen.add(k);
        labels.push(k);
      }
      map.set(k, (map.get(k) || 0) + num0(x[kgKey] ?? x.planned_kg ?? x.kg));
    }
  };
  add(remainingArr, rem, 'planned_kg');
  add(preparedArr, prep, 'kg');
  return labels
    .map(k => {
      const remaining = rem.get(k) || 0;
      const prepared = prep.get(k) || 0;
      return {
        label: k,
        remaining: r2(remaining),
        prepared: r2(prepared),
        diff: r2(Math.max(0, remaining - prepared))
      };
    })
    .sort((a, b) => b.remaining + b.prepared - (a.remaining + a.prepared));
};

// grouped bars: Remaining (orders) | [Prepared + Still-to-plan] stacked. Exported so
// the Future-Products page reuses it for the simulate charts.
export const groupedBarOption = (rows, labelFmt, tc) => ({
  grid: { left: 52, right: 14, top: 36, bottom: 46 },
  legend: {
    top: 2,
    textStyle: { fontSize: 11, color: tc('gray-700') },
    icon: 'roundRect',
    itemHeight: 9,
    itemWidth: 14
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
    valueFormatter: v => `${fmt(v)} kg`
  },
  xAxis: {
    type: 'category',
    data: rows.map(r => labelFmt(r.label)),
    axisLine: { lineStyle: { color: tc('gray-300') } },
    axisTick: { show: false },
    axisLabel: {
      fontSize: 10,
      color: tc('gray-700'),
      interval: 0,
      rotate: rows.length > 6 ? 30 : 0
    }
  },
  yAxis: {
    type: 'value',
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { fontSize: 10, color: tc('gray-600'), formatter: v => fmt(v) },
    splitLine: { lineStyle: { color: tc('gray-200'), type: 'dashed' } }
  },
  series: [
    {
      name: 'Remaining (orders)',
      type: 'bar',
      data: rows.map(r => r.remaining),
      itemStyle: { color: tc('primary'), borderRadius: [4, 4, 0, 0] },
      barMaxWidth: 22
    },
    {
      name: 'Prepared (future)',
      type: 'bar',
      stack: 'plan',
      data: rows.map(r => r.prepared),
      itemStyle: { color: tc('success') },
      barMaxWidth: 22
    },
    {
      name: 'Still to plan',
      type: 'bar',
      stack: 'plan',
      data: rows.map(r => r.diff),
      itemStyle: { color: tc('warning'), borderRadius: [4, 4, 0, 0] },
      barMaxWidth: 22
    }
  ]
});

const StatCard = ({ label, value, unit, icon, color }) => (
  <Card className="h-100">
    <Card.Body className="d-flex align-items-center gap-3">
      <div
        className="d-flex align-items-center justify-content-center rounded-3"
        style={{ width: 44, height: 44, background: color + '22', color }}
      >
        <FontAwesomeIcon icon={icon} />
      </div>
      <div>
        <div className="fs-9 text-700">{label}</div>
        <div className="fs-6 fw-bold">
          {value} <span className="fs-9 fw-normal text-700">{unit}</span>
        </div>
      </div>
    </Card.Body>
  </Card>
);

const FuturePreparedTopSection = () => {
  const { setGlobalFilter, getPrePaginationRowModel, getColumn } =
    useAdvanceTableContext();
  const tabItems = useMemo(() => {
    const rows = getPrePaginationRowModel().rows;
    const counts = new Map();
    for (const r of rows) {
      const n = String(r.original?.name || '').toUpperCase();
      if (n) counts.set(n, (counts.get(n) || 0) + 1);
    }
    const names = [...counts.keys()].sort((a, b) => a.localeCompare(b));
    return [
      {
        label: 'All',
        value: 'all',
        onClick: () => getColumn('product')?.setFilterValue(''),
        count: rows.length
      },
      ...names.map(n => ({
        label: n,
        value: n,
        onClick: () => getColumn('product')?.setFilterValue(n),
        count: counts.get(n)
      }))
    ];
  }, [getPrePaginationRowModel, getColumn]);
  return (
    <Row className="g-3 justify-content-between align-items-center px-3 pt-3 mb-2">
      <Col xs={12} sm="auto">
        <FilterTab tabItems={tabItems} />
      </Col>
      <Col xs={12} sm="auto">
        <SearchBox
          onChange={e => setGlobalFilter(e.target.value || undefined)}
          placeholder="Search product / section / client…"
          style={{ maxWidth: '26rem' }}
        />
      </Col>
    </Row>
  );
};

// SHARED summary: the 2 material charts + the prepared future-products table.
// Reused by the Future-Products page AND embedded under Project Bobin State.
const FuturePlanningSummary = ({ title = 'Future planning' }) => {
  const navigate = useNavigate();
  const { getThemeColor } = useAppContext();
  const { data: products = [], isLoading } = useFutureProducts();
  const { data: dash } = useFutureDashboard();
  const { data: remaining } = useRemainingMaterialSummary();
  const { remove, launch } = useFutureMutations();

  const totals = dash?.totals || {};
  const perProduct = useMemo(
    () => new Map((dash?.byProduct || []).map(p => [p.futureId, p])),
    [dash]
  );

  const copperRows = useMemo(
    () =>
      mergeMaterial(
        remaining?.copperByDiameter,
        dash?.copperByDiameter,
        'wireDiameter'
      ),
    [remaining, dash]
  );
  const pvcRows = useMemo(
    () => mergeMaterial(remaining?.pvcByGrade, dash?.pvcByGrade, 'grade'),
    [remaining, dash]
  );

  const futureColumns = useMemo(
    () => buildFutureColumns({ perProduct, launch, remove, navigate }),
    [perProduct, launch, remove, navigate]
  );
  const futureTable = useAdvanceTable({
    data: products,
    columns: futureColumns,
    pageSize: 8,
    pagination: true,
    sortable: true
  });

  return (
    <div>
      {title && (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <h4 className="mb-0">
            <FontAwesomeIcon icon={faFlask} className="me-2 text-primary" />
            {title}
          </h4>
          <span className="text-700 fs-9">
            Isolated sandbox — simulated, not launched. Copper / material vs
            remaining open orders + the products already prepared.
          </span>
        </div>
      )}

      <Row className="g-3 mb-3">
        <Col sm={6} xl={3}>
          <StatCard
            label="Products"
            value={totals.count ?? products.length}
            unit=""
            icon={faFlask}
            color="#3874ff"
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            label="Copper (prepared)"
            value={fmt(totals.copper_kg)}
            unit="kg"
            icon={faRocket}
            color="#b5651d"
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            label="PVC (prepared)"
            value={fmt(totals.pvc_kg)}
            unit="kg"
            icon={faRocket}
            color="#2f9e44"
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            label="BLR (prepared)"
            value={fmt(totals.steel_kg)}
            unit="kg"
            icon={faRocket}
            color="#868e96"
          />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col lg={5}>
          <Card className="h-100">
            <Card.Body>
              <h5 className="mb-1">Copper required</h5>
              <p className="text-700 fs-9 mb-2">
                Remaining orders vs prepared in future vs still to plan (by Ø)
              </p>
              {copperRows.length ? (
                <ReactECharts
                  option={groupedBarOption(
                    copperRows,
                    d => `Ø ${Number(d).toFixed(2)}`,
                    getThemeColor
                  )}
                  style={{ height: 300 }}
                />
              ) : (
                <div className="text-700 text-center py-6">No copper data</div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={7}>
          <Card className="h-100">
            <Card.Body>
              <h5 className="mb-1">Raw ingredients for future projects</h5>
              <p className="text-700 fs-9 mb-2">
                Remaining orders vs prepared in future vs still to plan (by grade)
              </p>
              {pvcRows.length ? (
                <ReactECharts
                  option={groupedBarOption(
                    pvcRows,
                    g => String(g || '—'),
                    getThemeColor
                  )}
                  style={{ height: 300 }}
                />
              ) : (
                <div className="text-700 text-center py-6">No material data</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header>
          <h4 className="mb-0">
            Prepared future products{' '}
            <span className="fw-normal text-700 fs-9">({products.length})</span>
          </h4>
        </Card.Header>
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="text-center py-5 text-700">
              <Spinner size="sm" /> Loading…
            </div>
          ) : !products.length ? (
            <div className="text-center py-6 text-700">
              No future products yet.
            </div>
          ) : (
            <AdvanceTableProvider {...futureTable}>
              <FuturePreparedTopSection />
              <div className="border-bottom">
                <AdvanceTable
                  tableProps={{
                    className:
                      'phoenix-table border-top border-200 fs-9 align-middle'
                  }}
                />
              </div>
              <AdvanceTableFooter pagination className="py-3" />
            </AdvanceTableProvider>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default FuturePlanningSummary;
